
import React, { useState } from 'react';
import { 
  Share2,
  Link as LinkIcon,
  Bookmark,
  Heart,
  MessageCircle
} from 'lucide-react';
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SharingService } from '@/services/sharing/sharingService';
import { useIsMobile } from '@/hooks/use-mobile';
import { useComments } from '@/hooks/social/useComments';
import { useDebates } from '@/hooks/social/useDebates';
import { useTranslation } from 'react-i18next';

interface SocialShareProps {
  isBookmarked?: boolean;
  isLiked?: boolean;
  articleNumber: number;
  context: string;
  question: string;
  answer: string;
  intervieweeName: string;
  title: string;
  company: string;
  interviewId: string;
}

const SocialShare = ({ 
  isBookmarked: initialBookmarked = false,
  isLiked: initialLiked = false,
  articleNumber,
  context,
  question,
  answer,
  intervieweeName,
  title,
  company,
  interviewId
}: SocialShareProps) => {
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked);
  const [isLiked, setIsLiked] = useState(initialLiked);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  
  // Get engagement data
  const { commentStats } = useComments(interviewId);
  const { debates } = useDebates(interviewId);
  
  // Calculate total engagement count
  const totalEngagement = (commentStats?.totalComments || 0) + (debates?.length || 0);

  const handleBookmark = async () => {
    try {
      setIsBookmarked(!isBookmarked);
      toast.success(isBookmarked ? t('success.bookmarkRemoved') : t('success.bookmarkAdded'));
    } catch (error) {
      console.error('Failed to update bookmark:', error);
      setIsBookmarked(!isBookmarked); // Revert on error
      toast.error(t('errors.bookmarkError'));
    }
  };

  const handleLike = async () => {
    try {
      setIsLiked(!isLiked);
      toast.success(isLiked ? t('success.likeRemoved') : t('success.likeAdded'));
    } catch (error) {
      console.error('Failed to update like:', error);
      setIsLiked(!isLiked); // Revert on error
      toast.error(t('errors.likeError'));
    }
  };

  const handleShare = async (platform: string) => {
    try {
      // Construct URL with dedicated article route
      const articleUrl = `${window.location.origin}/article/${interviewId}`;

      switch (platform) {
        case 'native':
          const shared = await SharingService.shareViaNavigator(document.title, articleUrl);
          if (shared) {
            toast.success(t('success.contentShared'));
          } else {
            toast.info("Share feature not supported on this browser");
          }
          break;
        case 'clipboard':
          await SharingService.copyToClipboard(articleUrl);
          toast.success(t('success.linkCopied'));
          break;
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error(t('errors.shareError'));
    }
  };

  return (
    <div className="flex -mx-1 text-gray-500">
      {/* Engagement/Comments indicator */}
      <div className="flex items-center gap-1 px-2 text-muted-foreground">
        <MessageCircle className={`${isMobile ? "w-4 h-4" : "w-5 h-5"}`} />
        <span className="text-xs font-medium">{totalEngagement}</span>
      </div>
      
      <Button 
        variant="ghost" 
        size="icon"
        onClick={handleLike}
        className="hover:text-[#ea384c] p-0 mx-0.5"
        aria-label={isLiked ? "Unlike" : "Like"}
      >
        <Heart 
          className={`w-4 h-4 ${isMobile ? "w-4 h-4" : "w-5 h-5"} ${isLiked ? "fill-[#ea384c] text-[#ea384c]" : ""}`}
        />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        onClick={handleBookmark}
        className="hover:text-[#ea384c] p-0 mx-0.5"
        aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
      >
        <Bookmark 
          className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} ${isBookmarked ? "fill-[#ea384c] text-[#ea384c]" : ""}`}
        />
      </Button>

      <Button 
        variant="ghost" 
        size="icon"
        onClick={() => handleShare('native')}
        className="hover:text-[#ea384c] p-0 mx-0.5"
      >
        <Share2 className={`${isMobile ? "w-4 h-4" : "w-5 h-5"}`} />
      </Button>

      <Button 
        variant="ghost" 
        size="icon"
        onClick={() => handleShare('clipboard')}
        className="hover:text-[#ea384c] p-0 mx-0.5"
      >
        <LinkIcon className={`${isMobile ? "w-4 h-4" : "w-5 h-5"}`} />
      </Button>
    </div>
  );
};

export default SocialShare;
